import React, { useState, useEffect } from 'react';
import { panelLabel } from '../../utils/panelLabels';
import { useSettingsReadOnly } from './readOnlyContext';

// V2 API sends: fan_status_virtual = true (Virtual) / false (Real)
// Convert to display label
const toDisplayLabel = (val) => {
  if (val === true || val === 'true') return 'Virtual';
  if (val === false || val === 'false') return 'Real';
  return 'Virtual';
};

const DigitalInputSetup = ({ getSettingValue, handleEdit }) => {
  const readOnly = useSettingsReadOnly();
  // Get initial value from settings
  const fanStatusValue = getSettingValue('fan_status').value;

  // Initialize state with current value
  const [fanStatus, setFanStatus] = useState(toDisplayLabel(fanStatusValue));
  const [initialFanStatus, setInitialFanStatus] = useState(toDisplayLabel(fanStatusValue));
  const [hasChanges, setHasChanges] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Update initial value when settings change
  useEffect(() => {
    const newFanStatus = toDisplayLabel(fanStatusValue);
    setInitialFanStatus(newFanStatus);
    setFanStatus(newFanStatus);
  }, [fanStatusValue]);

  // Check if there are changes
  useEffect(() => {
    setHasChanges(fanStatus !== initialFanStatus);
  }, [fanStatus, initialFanStatus]);

  const handleApply = async () => {
    if (!hasChanges) return;

    setIsApplying(true);
    try {
      // Boolean write, V2 semantics: true = Virtual, false = Real
      await handleEdit('fan_status', fanStatus === 'Virtual');

      // Update initial value after successful apply
      setInitialFanStatus(fanStatus);
      setHasChanges(false);
    } catch (error) {
      console.error('Error applying changes:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = () => {
    // Reset to initial value
    setFanStatus(initialFanStatus);
    setHasChanges(false);
  };


  return (
    <div className="h-full flex flex-col">
      <div className="flex-1">
        {/* Two option cards rather than a dropdown, the way the panel offers
            this choice. Each says what it actually does. */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-2xl">
          <p className="text-lg font-semibold text-gray-800 mb-4">
            {panelLabel('fan_status', 'Fan Status')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { value: 'Virtual', description: 'Software controlled' },
              { value: 'Real', description: 'Hardware input' }
            ].map((option) => {
              const active = fanStatus === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => !readOnly && setFanStatus(option.value)}
                  disabled={readOnly}
                  className={`rounded-lg border-2 p-5 text-left transition-colors ${
                    active
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } ${readOnly ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
                >
                  <p className={`text-lg font-bold ${active ? 'text-blue-700' : 'text-gray-700'}`}>
                    {option.value}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Buttons */}
      {!readOnly && (
      <div className="mt-auto pt-6 border-t border-gray-200">
        <div className="flex justify-end gap-4">
          <button
            onClick={handleCancel}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
            disabled={!hasChanges || isApplying}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!hasChanges || isApplying}
            className={`px-8 py-3 font-semibold rounded-lg transition-all ${
              hasChanges && !isApplying
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isApplying ? 'Applying...' : 'Apply Changes'}
          </button>
        </div>

        {/* Change Indicator */}
        {hasChanges && (
          <div className="mt-4 text-center">
            <p className="text-sm text-orange-600 font-medium">
              You have unsaved changes. Click Apply to save or Cancel to discard.
            </p>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default DigitalInputSetup;
